import cloneDeep from 'lodash/cloneDeep';
import { Editor } from '@meitu/saas-editor-core-next/es/editor';
import { collectFontSubsetRequests } from '@meitu/saas-editor-core-next/es/layers/text-layer/utils/font';
import { setLayerRuntimeValue } from '@meitu/saas-editor-core-next/es/utils/layer-kit';
import { traverseTrees } from '@meitu/saas-editor-core-next/es/utils/tree-kit';
import { LayerType, type ILayerProps } from '@meitu/saas-editor-core-next/es/version-types';
import { fetchFontList, type FontMaterialRaw } from '@meitu/xdesign-widgets';

const FILE_PARSER_EDITOR_ID = 'file-parser-editor';

const loadFileParserEditorPlugins = async () => {
    const [background, brush, group, image, qrCode, text, vector] = await Promise.all([
        import('@meitu/saas-editor-core-next/es/layers/background-layer'),
        import('@meitu/saas-editor-core-next/es/layers/brush-layer'),
        import('@meitu/saas-editor-core-next/es/layers/group-layer'),
        import('@meitu/saas-editor-core-next/es/layers/image-layer'),
        import('@meitu/saas-editor-core-next/es/layers/qr-code-layer'),
        import('@meitu/saas-editor-core-next/es/layers/text-layer'),
        import('@meitu/saas-editor-core-next/es/layers/vector-layer'),
    ]);

    return [
        { plugin: background.backgroundLayerPlugin },
        { plugin: brush.brushLayerPlugin },
        { plugin: group.groupLayerPlugin },
        { plugin: image.imageLayerPlugin },
        { plugin: qrCode.qrCodeLayerPlugin },
        { plugin: text.textLayerPlugin },
        { plugin: vector.vectorLayerPlugin },
    ];
};

let editor: Editor | null = null;
let editorReadyPromise: Promise<Editor> | null = null;
let configuredFonts: FontMaterialRaw[] | undefined;
let renderQueue: Promise<void> = Promise.resolve();

const getFileParserPublicURL = () => {
    const configuredPublicURL = process.env.PUBLIC_URL || './';
    return new URL(configuredPublicURL, document.baseURI).href;
};

const fetchFileParserFonts = async () => {
    const categories = await fetchFontList();
    return categories.flatMap((category) => category.materials);
};

/**
 * 按需创建并复用 file-parser 唯一的 Editor 实例。
 *
 * 显式传入字体时直接交给 FontService；未传时由 file-parser 请求业务字体目录。
 */
export const createFileParserEditor = async (fonts?: FontMaterialRaw[]) => {
    const shouldApplyFontsAfterReady = fonts !== undefined && editorReadyPromise !== null;
    if (fonts !== undefined) configuredFonts = fonts;

    if (!editorReadyPromise) {
        editorReadyPromise = (async () => {
            const instance = new Editor(FILE_PARSER_EDITOR_ID, {
                padding: 0,
                PUBLIC_URL: getFileParserPublicURL(),
            });

            try {
                instance.registerPlugins(await loadFileParserEditorPlugins());
                instance.create();

                await instance.services.textEngine.initialize();
                const fetchedFonts = configuredFonts === undefined ? await fetchFileParserFonts() : configuredFonts;
                instance.services.font.setRaw(configuredFonts ?? fetchedFonts);
                editor = instance;
                return instance;
            } catch (error) {
                instance.destroy();
                throw error;
            }
        })();

        void editorReadyPromise.catch(() => {
            editorReadyPromise = null;
        });
    }

    const instance = await editorReadyPromise;
    if (shouldApplyFontsAfterReady && fonts !== undefined) instance.services.font.setRaw(fonts);
    return instance;
};

/** 使用原始字体数据更新 file-parser Editor，字段转换统一由 FontService 负责。 */
export const setFileParserEditorFonts = async (fonts: FontMaterialRaw[]) => {
    configuredFonts = fonts;
    if (editor) {
        editor.services.font.setRaw(fonts);
        return;
    }
    await createFileParserEditor(fonts);
};

/**
 * 为尚未插入画布的文本图层加载字体，供文件解析测量和预览使用
 *
 * 将可用子集写入图层 runtime 的字体映射
 *
 * @param instance - 已初始化的文件解析编辑器
 * @param layer - 待测量或渲染的文本图层
 * @returns 字体加载及图层映射更新完成的 Promise
 */
export const loadFileParserTextLayerFonts = async (instance: Editor, layer: ILayerProps): Promise<void> => {
    await Promise.all(
        collectFontSubsetRequests(layer).map(async ({ fontFamily, chars }) => {
            const subsetFamily = await instance.services.font.loadSubset(fontFamily, chars);
            if (!subsetFamily) return;

            setLayerRuntimeValue(layer, {
                fontFamilyMap: {
                    ...layer.__runtime?.fontFamilyMap,
                    [fontFamily]: subsetFamily,
                },
            });
        }),
    );
};

export const renderLayersWithFileParserEditor = (layers: ILayerProps[], width: number, height: number) => {
    const task = renderQueue.then(async () => {
        const instance = await createFileParserEditor();
        const renderLayers = cloneDeep(layers);
        const textLayers: ILayerProps[] = [];

        instance.setViewport(width, height);
        traverseTrees(renderLayers, (layer) => {
            if (layer.layerType !== LayerType.TEXT_LAYER) return;

            instance.services.behavior.get(layer)?.onBeforeAddOrUpdate?.(layer);
            textLayers.push(layer);
        });
        await instance.services.font.loadFallbacks(renderLayers);
        await Promise.all(textLayers.map((layer) => loadFileParserTextLayerFonts(instance, layer)));
        await instance.services.stage.insertWithoutHistory([renderLayers], [{ width, height }]);

        return instance.services.result.drawLayers(renderLayers, 'png');
    });

    renderQueue = task.then(
        () => undefined,
        () => undefined,
    );
    return task;
};
